import {
  Injectable,
  Signal,
  WritableSignal,
  computed,
  signal,
} from '@angular/core';
import {
  SharedUserService,
  SubjectManageService,
} from '@g99-product-web-ui/shared-service';
import { Observable, catchError, debounceTime, of, tap } from 'rxjs';

import {
  NotificationReadStatus,
  NotificationTab,
} from '../constants/notification.constant';
import { NotificationReadStatusEnum } from '../enums/notification.enum';
import { IPaginationState } from '../interfaces/notification-facade.interface';
import {
  IGroupedNotifications,
  INotificationDto,
} from '../interfaces/notification.interface';
import { NotificationSocketService } from '../services/notification-socket.service';
import { NotificationService } from '../services/notification.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationFacade {
  // === Public signals for UI ===
  public readonly items: Signal<INotificationDto[]> = computed(
    () => this._getActiveState().items
  );

  // New computed signal for grouped notifications
  public readonly groupedNotifications: Signal<IGroupedNotifications> =
    computed(() => {
      const items = this._getActiveState().items;
      return this._groupNotificationsByDate(items);
    });

  public readonly isLoading: Signal<boolean> = computed(
    () => this._getActiveState().isLoading
  );

  public readonly hasMore: Signal<boolean> = computed(() => {
    const state = this._getActiveState();
    return state.pageIndex < state.totalPages;
  });

  public readonly hasUnread: Signal<boolean> = computed(() =>
    this._unreadState().items.some((n) => !n.seen)
  );

  public readonly hasUnseenDot = computed(() => this._unseenCount() > 0);

  public readonly _unseenCount = signal(0);
  public readonly unseenCount = this._unseenCount.asReadonly();
  public readonly _isPopoverOpen = signal(false);
  public readonly isPopoverOpen = this._isPopoverOpen.asReadonly();

  // === Signals ===
  private readonly _defaultPageSize = 20;

  private readonly _activeTab = signal<NotificationTab>('all');
  private readonly _recipientId = signal<number | undefined>(undefined);
  private readonly _unreadState: WritableSignal<IPaginationState> = signal(
    this._createInitialPaginationState()
  );

  private readonly _allState: WritableSignal<IPaginationState> = signal(
    this._createInitialPaginationState()
  );

  private readonly _reloadCounter = signal(0);

  constructor(
    private readonly _notificationService: NotificationService,
    private readonly _sharedUserService: SharedUserService,
    private readonly _notificationSocketService: NotificationSocketService,
    private readonly _subjectManagerService: SubjectManageService
  ) {
    const userId = this._sharedUserService.getCurrentUserId();
    if (userId) {
      this._recipientId.set(userId);
      this._seedUnseenCount(userId);
    }
    this._startNotificationSocket();

    // Dynamically react to user login, logout, and switch events immediately
    this._subjectManagerService.userSubject$.subscribe(() => {
      this._ensureRecipient();
    });
  }

  // === Public API ===

  public setPopoverOpenState(open: boolean): void {
    this._isPopoverOpen.set(open);
  }

  public setTab(tab: NotificationTab): void {
    if (this._activeTab() !== tab) {
      this._activeTab.set(tab);
      const state = this._getStateByTab(tab);
      state.set({
        ...this._createInitialPaginationState(),
        isLoading: true,
      });
    }
  }

  public loadMore(): void {
    const tab = this._activeTab();
    const state = this._getStateByTab(tab);
    const current = state();

    if (current.isLoading || current.pageIndex >= current.totalPages) {
      return;
    }

    const status: NotificationReadStatus | undefined =
      tab === 'unread' ? 'UNREAD' : undefined;

    state.set({ ...current, isLoading: true });

    this._notificationService
      .getNotifications(
        current.pageIndex,
        current.pageSize,
        status,
        this._recipientId()
      )
      .pipe(
        tap((response) => {
          if (!response?.data) {
            state.set({ ...current, isLoading: false });
            return;
          }

          const newItems = response.data;
          const merged = this._dedupeItems([...current.items, ...newItems]);

          state.set({
            ...current,
            pageIndex: (response.currentPage ?? current.pageIndex) + 1,
            totalPages: response.totalPages ?? current.totalPages,
            items: merged,
            isLoading: false,
          });
        }),
        catchError((err) => {
          console.error('Error loading more notifications', err);
          state.set({ ...current, isLoading: false });
          return of(null);
        })
      )
      .subscribe();
  }

  public markOneRead(notificationId: number): void {
    this._replaceInCaches((item) =>
      item.id === notificationId
        ? { ...item, status: NotificationReadStatusEnum.READ }
        : item
    );

    const unread = this._unreadState();
    this._unreadState.set({
      ...unread,
      items: unread.items.filter((i) => i.id !== notificationId),
    });

    this._notificationService
      .markNotificationRead(notificationId)
      .pipe(
        catchError((err) => {
          console.error('Failed to mark as read. Reloading...', err);
          this._triggerReload();
          return of(null);
        })
      )
      .subscribe();
  }

  public markAllRead(): Observable<void> {
    const userId = this._recipientId();
    if (!userId) {
      return of(void 0);
    } // Return an empty observable if no user

    // Optimistically update UI
    const all = this._allState();
    this._allState.set({
      ...all,
      items: all.items.map((item) => ({
        ...item,
        status: NotificationReadStatusEnum.READ,
      })),
    });

    this._unreadState.set({
      ...this._unreadState(),
      items: [],
    });

    this._unseenCount.set(0);

    return this._notificationService.markAllNotificationsRead(userId).pipe(
      tap(() => {
        this.refresh();
      }),
      catchError(() => {
        this.refresh(); // fallback to refresh anyway
        return of(void 0); // handle error gracefully
      })
    );
  }

  public refresh(): void {
    this._ensureRecipient();

    this._fetchNotifications();
  }

  public clearUnseenCount(): void {
    this._unseenCount.set(0);
  }

  /**
   * Ensure the in-app notification socket is connected and subscribed.
   * Safe to call repeatedly (start() is idempotent). Call this at post-login
   * so notifications are received app-wide regardless of which component renders.
   */
  public ensureRealtimeStarted(): void {
    this._notificationSocketService.start().subscribe();
  }

  // === Private ===

  private _groupNotificationsByDate(
    notifications: INotificationDto[]
  ): IGroupedNotifications {
    const today = new Date();
    const todayDateString = today.toDateString();

    const grouped: IGroupedNotifications = {
      today: [],
      older: [],
    };

    notifications.forEach((notification) => {
      const notificationDate = new Date(notification.createdAt);
      const notificationDateString = notificationDate.toDateString();

      if (notificationDateString === todayDateString) {
        grouped.today.push(notification);
      } else {
        grouped.older.push(notification);
      }
    });

    // Sort both groups by createdAt (newest first)
    grouped.today.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    grouped.older.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    return grouped;
  }

  private _ensureRecipient(): number | undefined {
    const fromService = this._sharedUserService.getCurrentUserId();
    const activeUserId = fromService || undefined;

    if (activeUserId !== this._recipientId()) {
      this._recipientId.set(activeUserId);

      // Reset state for the new/logged-out user
      this._allState.set(this._createInitialPaginationState());
      this._unreadState.set(this._createInitialPaginationState());
      this._unseenCount.set(0);

      // Reset WebSocket subscription
      this._notificationSocketService.stop();
      if (activeUserId) {
        this._notificationSocketService.start().subscribe();
        this._seedUnseenCount(activeUserId);
      }
    }

    return activeUserId;
  }

  private _fetchNotifications(): void {
    const recipientId = this._recipientId();
    if (!recipientId) {
      return;
    }
    const tab = this._activeTab();
    const state = this._getStateByTab(tab);
    const status: NotificationReadStatus | undefined =
      tab === 'unread' ? 'UNREAD' : undefined;

    this._notificationService
      .getNotifications(0, this._defaultPageSize, status, recipientId)
      .pipe(
        tap((response) => {
          if (!response?.data) {
            state.set({
              ...this._createInitialPaginationState(),
              isLoading: false,
            });
            return;
          }

          const items = response.data;
          state.set({
            pageIndex: 1,
            pageSize: this._defaultPageSize,
            totalPages: response.totalPages ?? 1,
            items: items as INotificationDto[],
            isLoading: false,
          });
        }),
        catchError((error) => {
          console.error('Error loading notifications', error);
          state.set({
            ...this._createInitialPaginationState(),
            isLoading: false,
          });
          return of(null);
        })
      )
      .subscribe();
  }

  private _getActiveState(): IPaginationState {
    return this._getStateByTab(this._activeTab())();
  }

  private _getStateByTab(
    tab: NotificationTab
  ): WritableSignal<IPaginationState> {
    return tab === 'unread' ? this._unreadState : this._allState;
  }

  private _createInitialPaginationState(): IPaginationState {
    return {
      pageIndex: 0,
      pageSize: this._defaultPageSize,
      totalPages: 1,
      items: [],
      isLoading: false,
    };
  }

  private _dedupeItems(items: INotificationDto[]): INotificationDto[] {
    const map = new Map<number, INotificationDto>();
    items.forEach((item) => map.set(item.id, item));
    return Array.from(map.values());
  }

  private _replaceInCaches(
    transform: (item: INotificationDto) => INotificationDto
  ): void {
    const all = this._allState();
    const unread = this._unreadState();

    this._allState.set({
      ...all,
      items: all.items.map(transform),
    });

    this._unreadState.set({
      ...unread,
      items: unread.items.map(transform),
    });
  }

  private _triggerReload(): void {
    this._reloadCounter.set(this._reloadCounter() + 1);
  }

  /**
   * One-shot seed of the unseen badge count on load / user change. The socket
   * only pushes on new events (not on connect), so the initial count for
   * already-unread notifications is read once from the notifications list
   * (UNREAD, size 1 -> totalElements). No polling loop; live updates come
   * from the socket.
   */
  private _seedUnseenCount(userId: number): void {
    this._notificationService
      .getNotifications(0, 1, 'UNREAD', userId)
      .pipe(catchError(() => of(null)))
      .subscribe((response) => {
        if (response?.totalElements != null) {
          this._unseenCount.set(response.totalElements);
        }
      });
  }

  /**
   * Real-time notification updates over the central WebSocket. Every socket
   * event payload contains the current unseenCount, which we use to update
   * the local state directly without calling any REST API.
   */
  private _startNotificationSocket(): void {
    if (!this._notificationSocketService.isEnabled()) {
      return;
    }

    this._notificationSocketService.start().subscribe();

    this._notificationSocketService.events$
      .pipe(debounceTime(300))
      .subscribe((message: unknown) => {
        // Keep the recipient in sync (resubscribe on login/switch) but do not
        // gate the badge on it — the frame arrives on the user's own topic.
        this._ensureRecipient();

        let payload = message;
        if (typeof message === 'string') {
          try {
            payload = JSON.parse(message);
          } catch {
            // Not a JSON string
          }
        }

        if (payload && typeof payload === 'object') {
          const raw = payload as Record<string, unknown>;
          const rawCount =
            raw['unseenCount'] ?? raw['unseen_count'] ?? raw['count'];
          // unseenCount may arrive as a number or a numeric string; coerce.
          const pushedCount = rawCount == null ? NaN : Number(rawCount);
          if (!Number.isNaN(pushedCount)) {
            this._unseenCount.set(pushedCount);
            if (this._isPopoverOpen()) {
              this.refresh();
            }
          }
        }
      });
  }
}
